import modal from '~components/modal';
import { CloseOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Col,
  Row,
  Form,
  Select,
  Input,
  Radio,
  Icon,
  Divider,
  DatePicker,
  message,
} from 'antd';
import EnhancedInputNumber from '~client/components/enhanced-input-number';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useLazyQuery } from '@apollo/client';
import { CREATE_NOTIFICATION, UPDATE_NOTIFICATION, QUERY_EST_NOTIFICATION } from '../queries';
import { displayLocaleOptions } from '~constants/property-field-options';
import { moment, dayFormatType } from '~helpers/day';
import AsyncCitySelector from '~client/components/async-city-selector';
import { PropertySearchSelector } from '~components/special-offer/map/offer-map-by-property/property-list-editor/components/select-property-by-property';
import CombinedTimePicker from './combined-time-picker';

export function formatSeconds(seconds) {
  if (typeof seconds !== 'number')
    return {
      days: null,
      hours: null,
      minutes: null,
    };

  const days = Math.floor(seconds / (3600 * 24));
  const hours = Math.floor((seconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  return {
    days,
    hours,
    minutes,
  };
}

function getSeconds(days, hours, minutes) {
  if (typeof days !== 'string' && typeof hours !== 'string' && typeof minutes !== 'string') {
    return null;
  }

  return (days || 0) * 24 * 3600 + (hours || 0) * 3600 + (minutes || 0) * 60;
}

export function calcIsCirculation(publishType) {
  if (['RANGE', 'PERMANENT'].includes(publishType)) {
    return true;
  }
  if (['ONE_TIME'].includes(publishType)) {
    return false;
  }
}
/*
  is loop:
  Time range (startTime- endTime) ——RANGE
  Permanent——PERMANENT
  not loop:
  Time range (only startTime)——ONE_TIME(startTime require)
  Right now——ONE_TIME (startTime require)
 */
function getPublishTime(publishType, publishTimes, publishTime) {
  const currentDateTime = moment().format(dayFormatType.APIPARAMS);

  if (publishType === 'RANGE') {
    return {
      start: publishTimes[0].format(dayFormatType.APIPARAMS),
      end: publishTimes[1].format(dayFormatType.APIPARAMS),
    };
  }
  if (publishType === 'PERMANENT') {
    return {
      start: null,
      end: null,
    };
  }
  if (publishType === 'ONE_TIME') {
    if (publishTime) {
      return {
        start: publishTime.format(dayFormatType.APIPARAMS),
        end: publishTime.format(dayFormatType.APIPARAMS),
      };
    } else {
      return {
        start: currentDateTime,
        end: currentDateTime,
      };
    }
  }
  if (publishType === 'RIGHT_NOW') {
    return {
      start: currentDateTime,
      end: currentDateTime,
    };
  }
}

/* Since the backend treats "Right now" and "Time range (only start time)" as the same type(ONE_TIME)
the frontend needs to perform a conversion.*/

function getPublishType(originPublishType) {
  if (originPublishType === 'RIGHT_NOW') {
    return 'ONE_TIME';
  }

  return originPublishType;
}

function getActiveTimeDefaultSelectedType(activeTime) {
  if (activeTime.start && !activeTime.end) {
    return 'futureRange';
  }

  return 'range';
}

function getTimeRangeOfOppLostAndActiveTime(combinedTimePickerValue) {
  if (!combinedTimePickerValue) {
    return {
      start: null,
      end: null,
    };
  }
  if (combinedTimePickerValue.selectedType === 'range') {
    if (combinedTimePickerValue.range?.[0] && combinedTimePickerValue.range?.[1]) {
      return {
        start: combinedTimePickerValue.range[0].startOf('day').format(dayFormatType.APIPARAMS),
        end: combinedTimePickerValue.range[1].endOf('day').format(dayFormatType.APIPARAMS),
      };
    }
    return {
      start: null,
      end: null,
    };
  }

  if (combinedTimePickerValue.selectedType === 'futureRange') {
    if (combinedTimePickerValue.futureRange[0]) {
      return {
        start: combinedTimePickerValue.futureRange[0]
          .startOf('day')
          .format(dayFormatType.APIPARAMS),
        end: null,
      };
    }
    return {
      start: null,
      end: null,
    };
  }

  if (combinedTimePickerValue.selectedType === 'last') {
    const days = combinedTimePickerValue.last;
    if (typeof days === 'number') {
      const endOfToday = moment().endOf('day');
      const startDay = endOfToday.clone().subtract(days, 'days').startOf('day');

      return {
        start: startDay.format(dayFormatType.APIPARAMS),
        end: endOfToday.format(dayFormatType.APIPARAMS),
      };
    }
    return {
      start: null,
      end: null,
    };
  }

  if (combinedTimePickerValue.selectedType === 'on') {
    const day = combinedTimePickerValue.on;
    if (day) {
      return {
        start: day.startOf('day').format(dayFormatType.APIPARAMS),
        end: day.endOf('day').format(dayFormatType.APIPARAMS),
      };
    }
    return {
      start: null,
      end: null,
    };
  }
}

const PublishTypeLoopOptions = [
  { label: 'Time range (start time - end time)', value: 'RANGE' },
  { label: 'Permanent', value: 'PERMANENT' },
];
const PublishTypeOneTimeOptions = [
  { label: 'Time range (only start time)', value: 'ONE_TIME' },
  { label: 'Right now', value: 'RIGHT_NOW' },
];

function NotificationEditor(props) {
  const { handleCloseModal, form, editingNotification } = props;
  const { getFieldDecorator } = form;
  const { t } = useTranslation();
  const [handleSubmit, { loading }] = useMutation(
    editingNotification ? UPDATE_NOTIFICATION : CREATE_NOTIFICATION,
  );

  const [applyFilters, setApplyFilter] = useState(() => {
    if (editingNotification) {
      const existFilter = [
        editingNotification.triggerCondition.registered === false && 'notRegistered',
        editingNotification.triggerCondition.destinationCity && 'destinationCity',
        editingNotification.triggerCondition.oppLost && 'oppLost',
        editingNotification.triggerCondition.activeTime && 'activeTime',
        'locales',
      ].filter(Boolean);
      return existFilter;
    }

    return ['locales'];
  });

  const isCirculation =
    typeof form.getFieldValue('isCirculation') === 'boolean'
      ? form.getFieldValue('isCirculation')
      : editingNotification?.publishType
      ? calcIsCirculation(editingNotification?.publishType)
      : null;

  const PublishTypeOptions = isCirculation ? PublishTypeLoopOptions : PublishTypeOneTimeOptions;

  const publishType = form.getFieldValue('publishType') || editingNotification?.publishType;
  const redirectType = form.getFieldValue('redirectType') || editingNotification?.redirectType;

  const publishTimesInitialValue = [
    editingNotification?.publishStart
      ? moment(editingNotification?.publishStart, moment.ISO_8601, true)
      : null,
    editingNotification?.publishEnd
      ? moment(editingNotification?.publishEnd, moment.ISO_8601, true)
      : null,
  ];

  const publishTimeInitialValue = editingNotification?.publishStart
    ? moment(editingNotification.publishStart)
    : null;

  const onSubmit = () => {
    form.validateFieldsAndScroll(async err => {
      if (err) {
        message.warn('Please check your input.');
        return;
      }
      const formData = form.getFieldsValue();

      const mutationVariables = {
        //Select students
        combinedBy: formData.combinedBy,
        triggerCondition: {
          ...(applyFilters.includes('notRegistered') ? { registered: false } : {}),
          locales: formData.locales,
          destinationCity: formData.destinationCity,
          oppLost: formData.oppLost,
          ...(formData.oppLost && {
            oppLost: {
              start: getTimeRangeOfOppLostAndActiveTime(formData.oppLost).start,
              end: getTimeRangeOfOppLostAndActiveTime(formData.oppLost).end,
              lost: true,
            },
          }),
          ...(formData.activeTime && {
            activeTime: {
              start: getTimeRangeOfOppLostAndActiveTime(formData.activeTime).start,
              end: getTimeRangeOfOppLostAndActiveTime(formData.activeTime).end,
            },
          }),
        },

        //Message setting
        name: formData.name,
        channels: [formData.channels],
        messageTitle: formData.messageTitle,
        messageContent: formData.messageContent,
        waitTime: getSeconds(formData.waitTimeDays, formData.waitTimeHours, formData.waitTimeMins),
        publishType: getPublishType(formData.publishType),
        rightNow: formData.publishType === 'RIGHT_NOW',
        // 为了方便使用所以拆成了两种，一种数组一种单个时间值
        // publishTimes: [formData.publishStart, formData.publishEnd],
        // publishTime: formData.publishStart,
        publishStart: getPublishTime(
          formData.publishType,
          formData.publishTimes,
          formData.publishTime,
        ).start,
        publishEnd: getPublishTime(
          formData.publishType,
          formData.publishTimes,
          formData.publishTime,
        ).end,

        //landing page
        redirectType: formData.redirectType,
        redirectValue: formData.redirectValue,
      };

      try {
        await handleSubmit({
          variables: {
            input: {
              ...(editingNotification ? { id: editingNotification?.id } : {}),
              ...(editingNotification ? { data: mutationVariables } : mutationVariables),
            },
          },
        });
        message.success('Create notification successfully');
        handleCloseModal(true);
      } catch (error) {
        if (error?.toString()?.includes('Tried to save duplicate unique keys')) {
          message.error(t('cms.marketing.notification.editor.validate.unique.name'));
        } else {
          message.error(error.toString());
        }
      }
    });
  };

  return (
    <div className="notification-editor-modal">
      <div className="modal__header">
        {t(`cms.marketing.notification.button.add`)}
        <CloseOutlined className="modal__close-button" onClick={handleCloseModal} />
      </div>
      <div className="modal__body">
        <section className="modal__color-mark-section">
          <h3>{t('cms.marketing.notification.editor.section.select_students')}</h3>
          <div className="notification-editor-modal__trigger-point-container">
            <div className="notification-editor-modal__trigger-point-container--left">
              <Form.Item
                required
                className="notification-editor-modal__trigger-point-container__left-label"
                label={t('cms.marketing.notification.viewer.item.filters_can_be_combined_by') + ':'}
              >
                {getFieldDecorator('combinedBy', {
                  rules: [{ required: true, message: t('cms.properties.edit.error.blank') }],
                  initialValue: editingNotification?.combinedBy || '_AND',
                  validateTrigger: 'onBlur',
                })(
                  <Radio.Group disabled className="w-full">
                    <div className="notification-editor-modal__trigger-point-container__radio-item">
                      <Radio value={'_AND'}>
                        {t('cms.marketing.notification.viewer.item.combined_by.and')}
                      </Radio>
                    </div>
                    <div className="notification-editor-modal__trigger-point-container__radio-item">
                      <Radio value={'_OR'}>
                        {t('cms.marketing.notification.viewer.item.combined_by.or')}
                      </Radio>
                    </div>
                  </Radio.Group>,
                )}
              </Form.Item>
            </div>

            <div className="notification-editor-modal__filter-container w-full">
              <Form.Item required label={t('cms.marketing.notification.editor.filter.title') + ':'}>
                <div className="notification-editor-modal__filter-container__checkbox-list">
                  <Checkbox.Group
                    value={applyFilters}
                    style={{ width: '100%' }}
                    onChange={v => setApplyFilter(v)}
                  >
                    <div className="notification-editor-modal__filter-container__checkbox-list__item">
                      <Checkbox value="notRegistered">
                        {t('cms.marketing.notification.viewer.item.not_register')}
                      </Checkbox>
                    </div>
                    <div className="notification-editor-modal__filter-container__checkbox-list__item">
                      <Checkbox value="destinationCity">
                        {t('cms.marketing.notification.viewer.item.destination_city')}
                      </Checkbox>
                    </div>
                    {applyFilters.includes('destinationCity') && (
                      <div className="notification-editor-modal__filter-container__checkbox-list__extra">
                        <Form.Item>
                          {getFieldDecorator('destinationCity', {
                            rules: [
                              { required: true, message: t('cms.properties.edit.error.blank') },
                            ],
                            initialValue: editingNotification?.triggerCondition?.destinationCity,
                            validateTrigger: 'onBlur',
                          })(
                            <AsyncCitySelector
                              className="w-full"
                              placeholder={t(
                                'cms.referral.promotion.modal.city_selector.placeholder',
                              )}
                              valueField="slug"
                            />,
                          )}
                        </Form.Item>
                      </div>
                    )}

                    <div className="notification-editor-modal__filter-container__checkbox-list__item">
                      <Checkbox disabled value="locales">
                        {t('cms.marketing.notification.viewer.item.student_language')}
                      </Checkbox>
                    </div>
                    {applyFilters.includes('locales') && (
                      <div className="notification-editor-modal__filter-container__checkbox-list__extra">
                        <Form.Item required>
                          {getFieldDecorator('locales', {
                            rules: [
                              { required: true, message: t('cms.properties.edit.error.blank') },
                            ],
                            initialValue: editingNotification?.triggerCondition?.locales,
                            validateTrigger: 'onBlur',
                          })(
                            <Select
                              mode="multiple"
                              className="w-full"
                              placeholder="Please select student language"
                            >
                              {displayLocaleOptions.map(item => (
                                <Option value={item.value.replace('-', '_')} key={item.value}>
                                  {item.name}
                                </Option>
                              ))}
                            </Select>,
                          )}
                        </Form.Item>
                      </div>
                    )}

                    <div className="notification-editor-modal__filter-container__checkbox-list__item">
                      <Checkbox value="oppLost">Opp lost</Checkbox>
                    </div>
                    {applyFilters.includes('oppLost') && (
                      <div className="notification-editor-modal__filter-container__checkbox-list__extra">
                        <Form.Item>
                          {getFieldDecorator('oppLost', {
                            initialValue: editingNotification?.triggerCondition?.oppLost
                              ? {
                                  selectedType: 'range',
                                  range: [
                                    editingNotification?.triggerCondition?.oppLost.start
                                      ? moment(editingNotification?.triggerCondition?.oppLost.start)
                                      : null,
                                    editingNotification?.triggerCondition?.oppLost.end
                                      ? moment(editingNotification?.triggerCondition?.oppLost.end)
                                      : null,
                                  ],
                                }
                              : {
                                  selectedType: 'range',
                                },
                            validateTrigger: 'onBlur',
                          })(<CombinedTimePicker />)}
                        </Form.Item>
                      </div>
                    )}

                    <div className="notification-editor-modal__filter-container__checkbox-list__item">
                      <Checkbox value="activeTime">Active time</Checkbox>
                    </div>
                    {applyFilters.includes('activeTime') && (
                      <div className="notification-editor-modal__filter-container__checkbox-list__extra">
                        <Form.Item>
                          {getFieldDecorator('activeTime', {
                            rules: [
                              {
                                validator: (_rule, value, callback) => {
                                  const validateTarget = value[value.selectedType];

                                  if (!validateTarget) {
                                    callback(t('cms.properties.edit.error.blank'));
                                  }

                                  if (
                                    typeof Array.isArray(validateTarget) &&
                                    validateTarget.length === 0
                                  ) {
                                    callback(t('cms.properties.edit.error.blank'));
                                  }

                                  callback();
                                },
                              },
                            ],
                            initialValue: editingNotification?.triggerCondition?.activeTime
                              ? {
                                  selectedType: getActiveTimeDefaultSelectedType(
                                    editingNotification?.triggerCondition?.activeTime,
                                  ),
                                  range: [
                                    editingNotification?.triggerCondition?.activeTime.start
                                      ? moment(
                                          editingNotification?.triggerCondition?.activeTime.start,
                                        )
                                      : null,
                                    editingNotification?.triggerCondition?.activeTime.end
                                      ? moment(
                                          editingNotification?.triggerCondition?.activeTime.end,
                                        )
                                      : null,
                                  ],
                                  futureRange: [
                                    editingNotification?.triggerCondition?.activeTime.start
                                      ? moment(
                                          editingNotification?.triggerCondition?.activeTime.start,
                                        )
                                      : null,
                                    null,
                                  ],
                                }
                              : {
                                  selectedType: 'range',
                                },
                            validateTrigger: 'onBlur',
                          })(<CombinedTimePicker enableActiveInFuture />)}
                        </Form.Item>
                      </div>
                    )}
                  </Checkbox.Group>
                </div>
              </Form.Item>
            </div>
          </div>
        </section>
        <section className="modal__color-mark-section">
          <h3>{t('cms.marketing.notification.editor.section.message_setting')}</h3>
          <Row gutter={32}>
            <Col span={12}>
              <Form.Item required label={t('cms.marketing.notification.editor.name') + ':'}>
                {getFieldDecorator('name', {
                  rules: [
                    { required: true, message: t('cms.properties.edit.error.blank') },
                    {
                      max: 50,
                      message: t('cms.marketing.notification.editor.validate.max_length', {
                        length: 50,
                      }),
                    },
                  ],
                  initialValue: editingNotification?.name,
                  validateTrigger: 'onBlur',
                })(<Input placeholder={t('')} />)}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item required label={t('cms.marketing.notification.viewer.item.channel') + ':'}>
                {getFieldDecorator('channels', {
                  rules: [{ required: true, message: t('cms.properties.edit.error.blank') }],
                  initialValue: editingNotification?.channels[0] || 'APP',
                  validateTrigger: 'onBlur',
                })(
                  <Select disabled className="w-full" placeholder="Select push channel">
                    <Select.Option value="APP">
                      {t('cms.marketing.notification.viewer.item.channel.app')}
                    </Select.Option>
                  </Select>,
                )}
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Form.Item
                required
                label={t('cms.marketing.notification.list.column.message_title') + ':'}
                extra={
                  <p className="notification-editor-modal__tip">
                    <Icon type="info-circle" />
                    {t('cms.marketing.notification.editor.tip.name')}
                  </p>
                }
              >
                {getFieldDecorator('messageTitle', {
                  rules: [
                    { required: true, message: t('cms.properties.edit.error.blank') },
                    {
                      max: 50,

                      message: t('cms.marketing.notification.editor.validate.max_length', {
                        length: 50,
                        field: 'full title',
                      }),
                    },
                  ],
                  initialValue: editingNotification?.messageTitle,
                  validateTrigger: 'onBlur',
                })(<Input />)}
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Form.Item
                required
                label={t('cms.marketing.notification.viewer.item.message_content') + ':'}
                extra={
                  <p className="notification-editor-modal__tip">
                    <Icon type="info-circle" />
                    {t('cms.marketing.notification.editor.tip.content')}
                  </p>
                }
              >
                {getFieldDecorator('messageContent', {
                  rules: [
                    { required: true, message: t('cms.properties.edit.error.blank') },
                    {
                      max: 110,
                      message: t('cms.marketing.notification.editor.validate.max_length', {
                        length: 110,
                        field: 'full content',
                      }),
                    },
                  ],
                  initialValue: editingNotification?.messageContent,
                  validateTrigger: 'onBlur',
                })(<Input.TextArea />)}
              </Form.Item>
            </Col>
          </Row>
          <Row className="notification-editor-modal__wait-time">
            <Form.Item
              label={t('cms.marketing.notification.viewer.item.wait_time') + ':'}
              extra={
                <p className="notification-editor-modal__tip">
                  <Icon type="info-circle" />
                  {t('cms.marketing.notification.editor.tip.wait_time')}
                </p>
              }
            >
              <Col span={8}>
                <Form.Item>
                  {getFieldDecorator('waitTimeDays', {
                    rules: [
                      {
                        validator: (_rule, value, callback) => {
                          if (Number(value) < 0) {
                            callback(t('cms.marketing.notification.editor.validate.positive'));
                          }
                          if (!Number.isInteger(Number(value))) {
                            callback(t('cms.marketing.notification.editor.validate.positive'));
                          }
                          callback();
                        },
                      },
                    ],
                    initialValue: formatSeconds(editingNotification?.waitTime).days,
                    validateTrigger: 'onBlur',
                  })(<EnhancedInputNumber addonAfter="day" placeholder="Day" />)}
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item>
                  {getFieldDecorator('waitTimeHours', {
                    rules: [
                      {
                        validator: (_rule, value, callback) => {
                          if (Number(value) < 0) {
                            callback(t('cms.marketing.notification.editor.validate.positive'));
                          }
                          if (Number(value) > 23) {
                            callback(t('cms.marketing.notification.editor.validate.max_hour'));
                          }
                          if (!Number.isInteger(Number(value))) {
                            callback(t('cms.marketing.notification.editor.validate.positive'));
                          }
                          callback();
                        },
                      },
                    ],
                    initialValue: formatSeconds(editingNotification?.waitTime).hours,
                    validateTrigger: 'onBlur',
                  })(<EnhancedInputNumber addonAfter="hr" placeholder="Hour" />)}
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item>
                  {getFieldDecorator('waitTimeMins', {
                    rules: [
                      {
                        validator: (_rule, value, callback) => {
                          if (Number(value) < 0) {
                            callback(t('cms.marketing.notification.editor.validate.positive'));
                          }
                          if (Number(value) > 59) {
                            callback(t('cms.marketing.notification.editor.validate.max_minutes'));
                          }
                          if (!Number.isInteger(Number(value))) {
                            callback(t('cms.marketing.notification.editor.validate.positive'));
                          }
                          callback();
                        },
                      },
                    ],
                    initialValue: formatSeconds(editingNotification?.waitTime).minutes,
                    validateTrigger: 'onBlur',
                  })(<EnhancedInputNumber addonAfter="min" placeholder="Min" />)}
                </Form.Item>
              </Col>
            </Form.Item>
          </Row>

          <Row>
            <Col span={24}>
              <Form.Item
                required
                label={t('cms.marketing.notification.editor.is_circulation') + ':'}
              >
                {getFieldDecorator('isCirculation', {
                  rules: [{ required: true, message: t('cms.properties.edit.error.blank') }],
                  initialValue: editingNotification?.publishType
                    ? calcIsCirculation(editingNotification.publishType)
                    : null,
                })(
                  <Radio.Group
                    onChange={() => {
                      if (form.getFieldValue('publishType')) {
                        form.setFieldsValue({
                          publishType: null,
                        });
                      }
                    }}
                    className="w-full"
                  >
                    <Radio value={true} className="full-radio-item">
                      {t('cms.marketing.notification.editor.is_circulation.yes')}
                    </Radio>
                    <Radio value={false} className="full-radio-item">
                      {t('cms.marketing.notification.editor.is_circulation.no')}
                    </Radio>
                  </Radio.Group>,
                )}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={32}>
            <Col span={12}>
              <Form.Item required label={t('cms.marketing.notification.editor.publish_type') + ':'}>
                {getFieldDecorator('publishType', {
                  rules: [{ required: true, message: t('cms.properties.edit.error.blank') }],
                  validateTrigger: 'onBlur',
                  initialValue: editingNotification?.publishType,
                })(
                  <Select className="w-full" placeholder="Please Select">
                    {PublishTypeOptions.map(option => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>,
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              {['RANGE', 'ONE_TIME'].includes(publishType) && (
                <Form.Item
                  required
                  label={t('cms.marketing.notification.editor.publish_time') + ':'}
                >
                  {isCirculation &&
                    getFieldDecorator('publishTimes', {
                      rules: [
                        {
                          type: 'array',
                          required: true,
                          message: t('cms.properties.edit.error.blank'),
                        },
                      ],
                      initialValue: publishTimesInitialValue,
                    })(<DatePicker.RangePicker showTime={{ minuteStep: 15 }} />)}

                  {!isCirculation &&
                    getFieldDecorator('publishTime', {
                      rules: [{ required: true, message: t('cms.properties.edit.error.blank') }],
                      initialValue: publishTimeInitialValue,
                    })(<DatePicker showTime={{ minuteStep: 15 }} className="w-full" />)}
                </Form.Item>
              )}
            </Col>
          </Row>
        </section>

        <EstEffectStudent form={form} applyFilters={applyFilters} />

        <section className="modal__color-mark-section">
          <h3>{t('cms.marketing.notification.editor.section.landing_page')}</h3>
          <Row gutter={32}>
            <Col span={12}>
              <Form.Item
                required
                label={t('cms.marketing.notification.viewer.item.landing_page_type') + ':'}
              >
                {getFieldDecorator('redirectType', {
                  rules: [{ required: true, message: t('cms.properties.edit.error.blank') }],
                  initialValue: editingNotification?.redirectType,
                  validateTrigger: 'onBlur',
                })(
                  <Select
                    className="w-full"
                    placeholder="Select page type"
                    onChange={() => form.resetFields(['redirectValue'])}
                  >
                    <Select.Option value="PP">
                      {t('cms.marketing.notification.viewer.item.landing_page_type.pp')}
                    </Select.Option>
                    <Select.Option value="LANDING_PAGE">
                      {t('cms.marketing.notification.viewer.item.landing_page_type.landing_page')}
                    </Select.Option>
                  </Select>,
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              {redirectType === 'PP' && (
                <Form.Item required label={t('cms.marketing.notification.editor.property') + ':'}>
                  {getFieldDecorator('redirectValue', {
                    rules: [{ required: true, message: t('cms.properties.edit.error.blank') }],
                    initialValue: editingNotification?.redirectValue,
                    validateTrigger: 'onBlur',
                    valuePropName: 'selectedProperty',
                  })(
                    <PropertySearchSelector
                      className="w-full"
                      propertyStatusFilter="ALL"
                      onSelect={(_id, pp) => {
                        form.setFieldsValue({
                          redirectValue: pp.slug,
                        });
                      }}
                    />,
                  )}
                </Form.Item>
              )}
              {redirectType === 'LANDING_PAGE' && (
                <Form.Item required label={t('cms.marketing.notification.editor.url') + ':'}>
                  {getFieldDecorator('redirectValue', {
                    rules: [
                      { required: true, message: t('cms.properties.edit.error.blank') },
                      { type: 'url', message: t('cms.marketing.notification.editor.validate.url') },
                    ],
                    initialValue: editingNotification?.redirectValue,
                    validateTrigger: 'onBlur',
                  })(<Input placeholder={t('Please enter url')} />)}
                </Form.Item>
              )}
            </Col>
          </Row>
        </section>
      </div>

      <div className="modal__footer">
        <Button
          size="large"
          className="promotion-plan-editor-container__cancel-button"
          onClick={handleCloseModal}
        >
          {t('cms.referral.promotion.modal.btn.cancel')}
        </Button>

        <Button
          type="primary"
          size="large"
          className="promotion-plan-editor-container__save-button"
          loading={loading}
          onClick={onSubmit}
        >
          {t('cms.properties.edit.btn.publish')}
        </Button>
      </div>
    </div>
  );
}

function EstEffectStudent({ form, applyFilters }) {
  const { t } = useTranslation();
  const [queryEstStudentNum, { data: queryEstStudentNumData }] = useLazyQuery(
    QUERY_EST_NOTIFICATION,
    {
      fetchPolicy: 'cache-and-network',
    },
  );

  const handleOnClick = () => {
    const destinationCity = form.getFieldValue('destinationCity');
    const locales = form.getFieldValue('locales');
    const notRegistered = applyFilters.includes('notRegistered');
    const oppLost = form.getFieldValue('oppLost');
    const oppLostDateRange = getTimeRangeOfOppLostAndActiveTime(oppLost);
    const activeTime = form.getFieldValue('activeTime');
    const activeTimeDateRange = getTimeRangeOfOppLostAndActiveTime(activeTime);

    queryEstStudentNum({
      variables: {
        filters: [
          ...(destinationCity
            ? [
                {
                  field: 'DESTINATION_CITY__VALUE',
                  value: form.getFieldValue('destinationCity'),
                },
              ]
            : []),
          ...(locales
            ? [
                {
                  field: 'LOCALE__VALUE',
                  op: 'IN',
                  value: form.getFieldValue('locales'),
                },
              ]
            : []),
          ...(notRegistered
            ? [
                {
                  field: 'REGISTERED__VALUE',
                  value: !notRegistered,
                },
              ]
            : []),
          ...(oppLost
            ? [
                {
                  field: 'OPP_LOST__VALUE',
                  op: 'EXACT',
                  value: true,
                },
                ...(oppLostDateRange.start && oppLostDateRange.end
                  ? [
                      {
                        field: 'OPP_LOST__OPP_LOST_DATETIME',
                        op: 'GTE',
                        value: oppLostDateRange.start,
                      },
                      {
                        field: 'OPP_LOST__OPP_LOST_DATETIME',
                        op: 'LT',
                        value: oppLostDateRange.end,
                      },
                    ]
                  : []),
              ]
            : []),
          ...(activeTime
            ? [
                {
                  field: 'ACTIVE_TIME',
                  op: 'GTE',
                  value: activeTimeDateRange.start,
                },
                ...(activeTimeDateRange.end
                  ? [
                      {
                        field: 'ACTIVE_TIME',
                        op: 'LT',
                        value: activeTimeDateRange.end,
                      },
                    ]
                  : []),
              ]
            : []),
        ],
      },
    });
  };
  return (
    <section className="modal__color-mark-section">
      <h3>{t('cms.marketing.notification.editor.section.estimate')}</h3>
      <div className="notification-editor-modal__estimate">
        <div className="notification-editor-modal__estimate__main">
          <Icon type="user" />
          <span>
            {queryEstStudentNumData ? null : '-'}
            {queryEstStudentNumData &&
              `${queryEstStudentNumData?.countSatisfyNotificationStudent?.count || 0} ${t(
                'cms.marketing.notification.editor.estimate.des',
              )}`}
          </span>
          <Divider type="vertical" />
          <button onClick={handleOnClick}>
            {t('cms.marketing.notification.editor.estimate.button')}
          </button>
        </div>
        <p className="notification-editor-modal__tip">
          <Icon type="info-circle" />
          {t('cms.marketing.notification.editor.tip.estimate')}
        </p>
      </div>
    </section>
  );
}

NotificationEditor.propTypes = {
  handleCloseModal: PropTypes.func.isRequired,
};

NotificationEditor.defaultProps = {
  handleCloseModal: () => {},
};

export default Form.create()(
  modal(
    {
      className: 'notification-editor-modal-container',
      useDefaultStyle: true,
    },
    true,
  )(NotificationEditor),
);
